import { useEffect, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiFilter,
  FiX,
  FiCheck,
  FiRefreshCw,
  FiPrinter,
  FiTrendingUp,
  FiDollarSign
} from "react-icons/fi";
import { FaClipboardList, FaPrint, FaUserTie } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const TransactionsPage = () => {
  const [allTransactions, setAllTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [lastPage, setLastPage] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);

  // Filter State
  const [filters, setFilters] = useState({
    employee_id: "",
    start_date: "",
    end_date: "",
    start_amount: "",
    end_amount: "",
  });

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const role = (localStorage.getItem("role") || "").toLowerCase();
      const managerOutletId = localStorage.getItem("id_outlet");
      const outletQueryParam =
        role === "manager" && managerOutletId ? `&outlet_id=${managerOutletId}` : "";

      const { employee_id, start_date, end_date, start_amount, end_amount } = filters;

      const filterParams = `
        ${employee_id ? `&employee_id=${employee_id}` : ""}
        ${start_date ? `&start_date=${start_date}` : ""}
        ${end_date ? `&end_date=${end_date}` : ""}
        ${start_amount ? `&start_amount=${start_amount}` : ""}
        ${end_amount ? `&end_amount=${end_amount}` : ""}
      `.replace(/\s+/g, "");

      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/transaction?page=${page}&per_page=${perPage}${outletQueryParam}${filterParams}`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();

      if (Array.isArray(json.data)) {
        setAllTransactions(json.data);
        setLastPage(json.meta?.last_page || 1);
        setTotalData(json.meta?.total || 0);
      } else {
        setAllTransactions([]);
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
      setAllTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/v1/employee?role=employee`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setEmployeeOptions(response.data.data || []);
      } catch (error) {
        console.error("Failed to fetch employees:", error);
      }
    };

    fetchEmployees();
  }, [token]);

  const fetchTransactionDetail = async (code) => {
    try {
      const encodedCode = encodeURIComponent(code);
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/transaction/${encodedCode}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) throw new Error("Gagal mengambil detail transaksi");

      const data = await res.json();

      if (data && data.data) {
        setSelectedTransaction(data.data);
        setShowModal(true);
      }
    } catch (err) {
      console.error("Error ambil detail:", err.message);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [page, filters]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const filtered = allTransactions.filter((trx) =>
    (trx.code || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalFilteredPages = Math.ceil(filtered.length / perPage);
  const validPage = Math.min(page, totalFilteredPages || 1);
  const paginated = filtered.slice((validPage - 1) * perPage, validPage * perPage);

  const totalValueSum = filtered.reduce((sum, item) => sum + (item.amount || 0), 0);

  const handleResetFilter = () => {
    const defaultFilters = {
      employee_id: "",
      start_date: "",
      end_date: "",
      start_amount: "",
      end_amount: "",
    };
    setFilters(defaultFilters);
    setShowFilter(false);
  };

  const handlePrintStruk = () => {
    if (!selectedTransaction) return;
    const printWin = window.open("", "_blank");
    if (!printWin) return;

    const itemsRows = (selectedTransaction.items || [])
      .map(
        (item) => `
        <tr>
          <td style="padding: 4px 0;">${item.name.toUpperCase()}</td>
          <td style="padding: 4px 0; text-align: center;">${item.qty} x Rp ${(item.price || 0).toLocaleString("id-ID")}</td>
          <td style="padding: 4px 0; text-align: right; font-weight: bold;">Rp ${(item.subtotal || 0).toLocaleString("id-ID")}</td>
        </tr>
      `
      )
      .join("");

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Struk Transaksi - ${selectedTransaction.code}</title>
          <style>
            body { font-family: 'Courier New', Courier, monospace; width: 280px; margin: 0 auto; padding: 15px; color: #000; font-size: 11px; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .bold { font-weight: bold; }
            .dashed-line { border-top: 1px dashed #000; margin: 8px 0; }
            table { width: 100%; font-size: 11px; border-collapse: collapse; }
          </style>
        </head>
        <body>
          <div class="text-center bold" style="font-size: 14px;">APOTEK DJAYA MEDIKA</div>
          <div class="text-center">${selectedTransaction.outlet_name || "Apotek Utama"}</div>
          <div class="text-center">${selectedTransaction.outlet_phone || "-"}</div>
          
          <div class="dashed-line"></div>
          <div>Kode Transaksi : ${selectedTransaction.code}</div>
          <div>Kasir          : ${selectedTransaction.created_by || "Kasir"}</div>
          <div>Tanggal        : ${new Date(selectedTransaction.created_at).toLocaleString("id-ID")}</div>
          <div class="dashed-line"></div>

          <table>
            ${itemsRows}
          </table>

          <div class="dashed-line"></div>
          <table>
            ${
              selectedTransaction.tuslah > 0
                ? `<tr><td>Tuslah</td><td class="text-right">Rp ${(selectedTransaction.tuslah || 0).toLocaleString("id-ID")}</td></tr>`
                : ""
            }
            ${
              selectedTransaction.embalase > 0
                ? `<tr><td>Embalase</td><td class="text-right">Rp ${(selectedTransaction.embalase || 0).toLocaleString("id-ID")}</td></tr>`
                : ""
            }
            <tr className="bold">
              <td>TOTAL NOMINAL</td>
              <td class="text-right" style="font-size: 13px;">Rp ${(selectedTransaction.total || selectedTransaction.amount || 0).toLocaleString("id-ID")}</td>
            </tr>
          </table>

          <div class="dashed-line"></div>
          <div class="text-center">
            <p style="margin: 4px 0;">Terima Kasih Atas Kunjungan Anda</p>
            <p style="margin: 0; font-size: 9px;">Semoga Lekas Sembuh</p>
          </div>
        </body>
      </html>
    `;

    printWin.document.write(html);
    printWin.document.close();
    printWin.print();
  };

  return (
    <div className="space-y-6 pb-12">
      {/* 1. Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Transaksi
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {loading ? "..." : totalData}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FaClipboardList className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Omset Transaksi (Halaman)
            </p>
            <h2 className="text-2xl font-extrabold text-emerald-700 font-mono">
              Rp {loading ? "..." : totalValueSum.toLocaleString("id-ID")}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
            <FiDollarSign className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Halaman Aktif
            </p>
            <h2 className="text-3xl font-extrabold text-emerald-600">
              {page} <span className="text-sm font-normal text-gray-400">/ {lastPage}</span>
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiTrendingUp className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* 2. Control Bar & Table */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="relative w-full md:w-80">
            <FiFileText className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari kode transaksi..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold"
            />
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto justify-end">
            <button
              onClick={() => setShowFilter(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm"
            >
              <FiFilter className="w-3.5 h-3.5 text-emerald-600" />
              <span>Filter Transaksi</span>
            </button>
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5">Kode Transaksi</th>
                  <th className="p-3.5">Kasir / Petugas</th>
                  <th className="p-3.5">Tanggal & Jam</th>
                  <th className="p-3.5 text-right">Tuslah</th>
                  <th className="p-3.5 text-right">Embalase</th>
                  <th className="p-3.5 text-right">Total Transaksi</th>
                  <th className="p-3.5 text-center">Aksi Struk</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-12 text-center text-gray-400 font-semibold">
                      Belum ada transaksi ditemukan
                    </td>
                  </tr>
                ) : (
                  paginated.map((trx) => (
                    <tr key={trx.id} className="hover:bg-emerald-50/20 transition-colors">
                      <td className="p-3.5 font-mono font-extrabold text-gray-900 text-sm">
                        {trx.code}
                      </td>
                      <td className="p-3.5 font-semibold text-gray-700">
                        {trx.created_by || "Kasir"}
                      </td>
                      <td className="p-3.5 font-mono text-gray-600">
                        {new Date(trx.created_at).toLocaleString("id-ID")}
                      </td>
                      <td className="p-3.5 text-right font-mono font-semibold text-gray-700">
                        Rp {(trx.tuslah || 0).toLocaleString("id-ID")}
                      </td>
                      <td className="p-3.5 text-right font-mono font-semibold text-gray-700">
                        Rp {(trx.embalase || 0).toLocaleString("id-ID")}
                      </td>
                      <td className="p-3.5 text-right font-mono font-extrabold text-emerald-700 text-sm">
                        Rp {(trx.amount || 0).toLocaleString("id-ID")}
                      </td>
                      <td className="p-3.5 text-center">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => fetchTransactionDetail(trx.code)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl text-xs font-bold transition-all"
                        >
                          <FiFileText className="w-3.5 h-3.5" />
                          <span>Lihat Struk</span>
                        </motion.button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <p className="text-gray-400">
            Menampilkan <span className="font-bold text-gray-700">{filtered.length}</span> dari{" "}
            <span className="font-bold text-gray-700">{totalData}</span> transaksi
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page <= 1}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                page <= 1
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-semibold text-gray-600 px-2">
              Halaman {page} dari {lastPage}
            </span>
            <button
              onClick={() => setPage((prev) => Math.min(prev + 1, lastPage))}
              disabled={page >= lastPage}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                page >= lastPage
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* MODAL STRUK STRUK THERMAL */}
      <AnimatePresence>
        {showModal && selectedTransaction && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 flex flex-col"
            >
              <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
                <div className="flex items-center gap-2">
                  <FiFileText className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-sm font-bold text-gray-900">Struk Transaksi Penjualan</h3>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1.5 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/80 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              {/* Thermal Receipt Preview */}
              <div className="p-6 font-mono text-xs bg-amber-50/20 text-gray-800 space-y-3 max-h-[60vh] overflow-y-auto">
                <div className="text-center space-y-0.5">
                  <h4 className="font-extrabold text-sm text-gray-900 uppercase">
                    APOTEK DJAYA MEDIKA
                  </h4>
                  <p className="text-[10px] text-gray-500">{selectedTransaction.outlet_name || "Apotek Utama"}</p>
                  <p className="text-[10px] text-gray-500">{selectedTransaction.outlet_phone || "-"}</p>
                </div>

                <div className="border-t border-dashed border-gray-300 pt-2 space-y-1 text-[11px]">
                  <div className="flex justify-between">
                    <span>No Kode:</span>
                    <span className="font-bold">{selectedTransaction.code}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Kasir:</span>
                    <span>{selectedTransaction.created_by || "Staff"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tanggal:</span>
                    <span>{new Date(selectedTransaction.created_at).toLocaleString("id-ID")}</span>
                  </div>
                </div>

                <div className="border-t border-dashed border-gray-300 pt-2 space-y-2 text-[11px]">
                  {(selectedTransaction.items || []).map((item) => (
                    <div key={item.id} className="space-y-0.5">
                      <p className="font-bold text-gray-900">{item.name.toUpperCase()}</p>
                      <div className="flex justify-between text-gray-600 text-[10px]">
                        <span>
                          {item.qty} x Rp {(item.price || 0).toLocaleString("id-ID")}
                        </span>
                        <span className="font-bold text-gray-800">
                          Rp {(item.subtotal || 0).toLocaleString("id-ID")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-dashed border-gray-300 pt-2 space-y-1 text-[11px]">
                  {selectedTransaction.tuslah > 0 && (
                    <div className="flex justify-between">
                      <span>Biaya Tuslah:</span>
                      <span>Rp {(selectedTransaction.tuslah || 0).toLocaleString("id-ID")}</span>
                    </div>
                  )}
                  {selectedTransaction.embalase > 0 && (
                    <div className="flex justify-between">
                      <span>Biaya Embalase:</span>
                      <span>Rp {(selectedTransaction.embalase || 0).toLocaleString("id-ID")}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-extrabold text-emerald-800 pt-1 text-sm border-t border-gray-300">
                    <span>TOTAL NOMINAL</span>
                    <span>Rp {(selectedTransaction.total || selectedTransaction.amount || 0).toLocaleString("id-ID")}</span>
                  </div>
                </div>

                <div className="text-center pt-3 border-t border-dashed border-gray-300 text-[10px] text-gray-500 space-y-0.5">
                  <p className="font-bold text-gray-700">Terima Kasih Atas Kunjungan Anda</p>
                  <p>Semoga Lekas Sembuh</p>
                </div>
              </div>

              <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50/50">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2.5 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-semibold transition-colors"
                >
                  Tutup
                </button>
                <button
                  onClick={handlePrintStruk}
                  className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-1.5"
                >
                  <FaPrint className="w-3.5 h-3.5" />
                  <span>Cetak Struk</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL FILTER */}
      <AnimatePresence>
        {showFilter && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-gray-100 flex flex-col"
            >
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-500/20">
                    <FiFilter className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Filter Transaksi</h2>
                    <p className="text-xs text-gray-500">Saring berdasarkan kasir, tanggal, dan nominal</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowFilter(false)}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/80 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 text-xs">
                <div className="space-y-1.5">
                  <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider flex items-center gap-1.5">
                    <FaUserTie className="text-emerald-600" /> Kasir / Petugas
                  </label>
                  <select
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                    value={filters.employee_id}
                    onChange={(e) => setFilters({ ...filters, employee_id: e.target.value })}
                  >
                    <option value="">Semua Employee Kasir</option>
                    {employeeOptions.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.full_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider">
                    Rentang Tanggal
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      className="p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                      value={filters.start_date}
                      onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                    />
                    <input
                      type="date"
                      className="p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                      value={filters.end_date}
                      onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider">
                    Rentang Nominal Transaksi (Rp)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Mulai..."
                      className="p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                      value={filters.start_amount}
                      onChange={(e) => setFilters({ ...filters, start_amount: e.target.value })}
                    />
                    <input
                      type="number"
                      placeholder="Maksimal..."
                      className="p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                      value={filters.end_amount}
                      onChange={(e) => setFilters({ ...filters, end_amount: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50/50">
                <button
                  onClick={handleResetFilter}
                  className="px-4 py-2.5 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-semibold transition-colors flex items-center gap-1.5"
                >
                  <FiRefreshCw className="w-3.5 h-3.5" />
                  <span>Reset</span>
                </button>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowFilter(false)}
                    className="px-4 py-2.5 rounded-2xl bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 text-xs font-semibold transition-colors"
                  >
                    Batal
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setPage(1);
                      fetchTransactions();
                      setShowFilter(false);
                    }}
                    className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-1.5"
                  >
                    <FiCheck className="w-4 h-4" />
                    <span>Terapkan</span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TransactionsPage;
